import clsx, { ClassValue } from 'clsx';
import Link from 'next/link';
import { LegacyRef, ReactNode } from 'react';

import styles from './Button.module.css';

export type ButtonStyle =
  | 'Primary'
  | 'Secondary'
  | 'SecondaryTeal'
  | 'Tertiary'
  | 'SingleInputForm';

export type ButtonSize = 'Small' | 'Medium' | 'Large';

interface ButtonProps {
  buttonSize?: ButtonSize;
  buttonStyle?: ButtonStyle;
  children: string | ReactNode;
  className?: string | ClassValue[];
  disabled?: boolean;
  /** Whether the button should render as full width. This can either be done with width: 100% or flex:1 depending on
   * the parent container of the button
   */
  fullWidth?: boolean | 'flex1';
  minWidth?: number;
  href?: string;
  id?: string;
  loading?: boolean;
  type?: 'button' | 'submit';
  onClick?: () => void;
  refAnchor?: LegacyRef<HTMLAnchorElement>;
  refButton?: LegacyRef<HTMLButtonElement>;
}

const Button = ({
  buttonSize = 'Medium',
  buttonStyle = 'Primary',
  children,
  className,
  disabled = false,
  fullWidth,
  href,
  id,
  loading,
  type = 'button',
  onClick,
  minWidth,
  refAnchor,
  refButton,
}: ButtonProps) => {
  const classNames = clsx(
    styles.daymadeButton,
    styles['buttonSize__' + buttonSize],
    styles['buttonStyle__' + buttonStyle],
    loading ? styles.loading : '',
    disabled ? styles.disabled : '',
    fullWidth === true ? styles.fullWidth : '',
    fullWidth === 'flex1' ? styles.flex1 : '',
    className,
  );

  return href ? (
    <Link
      href={href}
      className={classNames}
      id={id}
      onClick={onClick}
      style={{ minWidth: minWidth }}
      ref={refAnchor}
    >
      <span className={styles.buttonInner}>{children}</span>
    </Link>
  ) : (
    <button
      type={type}
      className={classNames}
      onClick={onClick ? onClick : () => {}}
      id={id}
      disabled={disabled || loading}
      ref={refButton}
    >
      <span className={styles.buttonInner}>{children}</span>
    </button>
  );
};

export default Button;
